import { SystemIcon } from '@velocity/icons/system';
import { Button, TButtonVariant } from '@velocity/ui';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../redux/types';
import Navigation from '../../../../utils/navigation';

export interface ButtonGroupButtonProps {
  children?: React.ReactNode;
  /** Velocity button variant. Defaults to `secondary-filled` (main CTA). */
  variant?: TButtonVariant;
  StartIcon?: SystemIcon;
  type?: 'button' | 'submit';
  name?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  dataTestId?: string;
}

interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  type: 'default' | 'back-next';
  buttonOneProps: ButtonGroupButtonProps;
  buttonTwoProps?: ButtonGroupButtonProps;
  /** Hides the "zurück" button in the back-next bar (e.g. first step). */
  hideBackButton?: boolean;
}

const GroupButton: React.FC<ButtonGroupButtonProps> = ({
  variant = 'secondary-filled',
  type = 'button',
  dataTestId,
  children,
  ...props
}) => (
  <Button
    {...props}
    variant={variant}
    size='m'
    type={type}
    data-testid={dataTestId}
  >
    {children}
  </Button>
);

const ButtonGroup: React.FC<ButtonGroupProps> = ({
  type,
  buttonOneProps,
  buttonTwoProps,
  hideBackButton = false,
  className = '',
  ...props
}) => {
  // Sequence storage
  const { isStatusOverviewOpen } = useSelector(
    (state: RootState) => state.sequence,
  );

  // Navigate hook
  const navigate = useNavigate();

  // Navigation class
  const { previousRoute } = new Navigation();

  // Back button action
  const goBack = () => navigate(previousRoute());

  if (type === 'back-next') {
    const showBackButton = !hideBackButton && !isStatusOverviewOpen;
    return (
      <div className='back-next-container'>
        <div {...props} className={`button-group ${className}`}>
          {showBackButton && (
            <GroupButton
              type='button'
              variant='dark-outlined'
              className='back-btn'
              dataTestId='back'
              onClick={() => goBack()}
            >
              zurück
            </GroupButton>
          )}
          <GroupButton
            {...buttonOneProps}
            className={`next-btn ${buttonOneProps.className ?? ''}`}
          />
          {buttonTwoProps && <GroupButton {...buttonTwoProps} />}
        </div>
      </div>
    );
  }

  return (
    <div {...props} className={`button-group default ${className}`}>
      {buttonTwoProps && <GroupButton {...buttonTwoProps} />}
      <GroupButton {...buttonOneProps} />
    </div>
  );
};

export default ButtonGroup;
